import { useDeferredValue, useEffect, useState } from 'react';
import DashboardContentTable from '../../components/content/DashboardContentTable';
import ContentEditorForm from '../../components/dashboard/ContentEditorForm';
import LoadingState from '../../components/common/LoadingState';
import { useAuth } from '../../hooks/useAuth';
import {
  collectStoragePaths,
  createContentDocument,
  deleteContentDocument,
  listContent,
  updateContentDocument,
} from '../../services/contentService';
import {
  deleteStoragePath,
  uploadContentFile,
} from '../../services/mediaService';
import { USER_ROLES } from '../../services/userService';
import { getContentConfig } from '../../utils/contentConfig';

async function uploadMultipleFiles({
  files,
  collectionKey,
  documentId,
  folder,
  setUploadStatus,
}) {
  const uploads = [];

  for (const file of files) {
    const result = await uploadContentFile({
      collectionKey,
      documentId,
      file,
      folder,
      onProgress: (progress) =>
        setUploadStatus(`Uploading ${file.name} (${progress}%)`),
    });

    uploads.push({
      name: result.name,
      url: result.downloadURL,
      path: result.fullPath,
      contentType: result.contentType,
      size: result.size,
    });
  }

  return uploads;
}

async function buildMediaPatch({
  collectionKey,
  files,
  payload,
  documentId,
  currentItem,
  setUploadStatus,
}) {
  const patch = {};

  if (collectionKey === 'articles') {
    if (files.coverImageFile) {
      if (currentItem?.coverImagePath) {
        await deleteStoragePath(currentItem.coverImagePath);
      }

      const upload = await uploadContentFile({
        collectionKey,
        documentId,
        folder: 'cover-image',
        file: files.coverImageFile,
        onProgress: (progress) =>
          setUploadStatus(`Uploading cover image (${progress}%)`),
      });

      patch.coverImageUrl = upload.downloadURL;
      patch.coverImagePath = upload.fullPath;
    }

    if (files.relatedMediaFiles.length > 0) {
      const newItems = await uploadMultipleFiles({
        files: files.relatedMediaFiles,
        collectionKey,
        documentId,
        folder: 'related-media',
        setUploadStatus,
      });

      patch.relatedMedia = [...(currentItem?.relatedMedia || []), ...newItems];
    }

    const nextSections = Array.isArray(payload?.articleSections)
      ? payload.articleSections.map((section) => ({ ...section }))
      : [];
    const currentSections = Array.isArray(currentItem?.articleSections)
      ? currentItem.articleSections
      : [];

    // Remove orphaned section images when sections are deleted from the form.
    const nextSectionPaths = new Set(
      nextSections.map((section) => section?.imagePath).filter(Boolean)
    );
    let removedSectionImages = false;
    for (const section of currentSections) {
      if (section?.imagePath && !nextSectionPaths.has(section.imagePath)) {
        await deleteStoragePath(section.imagePath);
        removedSectionImages = true;
      }
    }

    const sectionImageFiles = Array.isArray(files.articleSectionImageFiles)
      ? files.articleSectionImageFiles
      : [];
    const hasSectionImageUploads = sectionImageFiles.some(Boolean);

    if (hasSectionImageUploads) {
      for (let index = 0; index < sectionImageFiles.length; index += 1) {
        const sectionFile = sectionImageFiles[index];
        if (!sectionFile) {
          continue;
        }

        const currentSection = nextSections[index] || {};
        if (currentSection.imagePath) {
          await deleteStoragePath(currentSection.imagePath);
        }

        const upload = await uploadContentFile({
          collectionKey,
          documentId,
          folder: `article-sections/section-${index + 1}`,
          file: sectionFile,
          onProgress: (progress) =>
            setUploadStatus(
              `Uploading section ${index + 1} image (${progress}%)`
            ),
        });

        nextSections[index] = {
          ...currentSection,
          imageUrl: upload.downloadURL,
          imagePath: upload.fullPath,
        };
      }
    }

    if (hasSectionImageUploads || removedSectionImages) {
      patch.articleSections = nextSections;
    }
  }

  if (collectionKey === 'animations') {
    if (files.mediaFile) {
      if (currentItem?.mediaPath) {
        await deleteStoragePath(currentItem.mediaPath);
      }

      const mediaUpload = await uploadContentFile({
        collectionKey,
        documentId,
        folder: 'media',
        file: files.mediaFile,
        onProgress: (progress) => setUploadStatus(`Uploading media (${progress}%)`),
      });

      patch.mediaUrl = mediaUpload.downloadURL;
      patch.mediaPath = mediaUpload.fullPath;
    }

    if (files.thumbnailFile) {
      if (currentItem?.thumbnailPath) {
        await deleteStoragePath(currentItem.thumbnailPath);
      }

      const thumbnailUpload = await uploadContentFile({
        collectionKey,
        documentId,
        folder: 'thumbnail',
        file: files.thumbnailFile,
        onProgress: (progress) =>
          setUploadStatus(`Uploading thumbnail (${progress}%)`),
      });

      patch.thumbnailUrl = thumbnailUpload.downloadURL;
      patch.thumbnailPath = thumbnailUpload.fullPath;
    }
  }

  if (collectionKey === 'models') {
    if (files.courseVideoFile) {
      if (currentItem?.videoPath) {
        await deleteStoragePath(currentItem.videoPath);
      }

      const videoUpload = await uploadContentFile({
        collectionKey,
        documentId,
        folder: 'video',
        file: files.courseVideoFile,
        onProgress: (progress) =>
          setUploadStatus(`Uploading course video (${progress}%)`),
      });

      patch.videoUrl = videoUpload.downloadURL;
      patch.videoPath = videoUpload.fullPath;
    }

    if (files.previewImageFile) {
      if (currentItem?.previewImagePath) {
        await deleteStoragePath(currentItem.previewImagePath);
      }

      const previewUpload = await uploadContentFile({
        collectionKey,
        documentId,
        folder: 'preview',
        file: files.previewImageFile,
        onProgress: (progress) =>
          setUploadStatus(`Uploading preview image (${progress}%)`),
      });

      patch.previewImageUrl = previewUpload.downloadURL;
      patch.previewImagePath = previewUpload.fullPath;
    }

    if (files.attachmentFiles.length > 0) {
      const uploads = await uploadMultipleFiles({
        files: files.attachmentFiles,
        collectionKey,
        documentId,
        folder: 'attachments',
        setUploadStatus,
      });
      patch.attachments = [...(currentItem?.attachments || []), ...uploads];
    }
  }

  return patch;
}

export default function DashboardCollectionPage({ collectionKey }) {
  const config = getContentConfig(collectionKey);
  const { user, profile } = useAuth();

  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const deferredSearchTerm = useDeferredValue(searchTerm);
  const canDelete = profile?.role === USER_ROLES.ADMIN;

  useEffect(() => {
    let isSubscribed = true;

    async function loadItems() {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await listContent(collectionKey);
        if (!isSubscribed) {
          return;
        }
        setItems(response);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }
        setErrorMessage(error.message ?? 'Failed to load records.');
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    }

    loadItems();

    return () => {
      isSubscribed = false;
    };
  }, [collectionKey]);

  const filteredItems = items.filter((item) => {
    if (!deferredSearchTerm.trim()) {
      return true;
    }

    const searchValue = deferredSearchTerm.toLowerCase();
    return (
      item.title?.toLowerCase().includes(searchValue) ||
      item.summary?.toLowerCase().includes(searchValue) ||
      item.slug?.toLowerCase().includes(searchValue)
    );
  });

  async function reloadItems() {
    const response = await listContent(collectionKey);
    setItems(response);
    return response;
  }

  async function handleSubmit({ id, payload, files }) {
    if (!user) {
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setUploadStatus('');

    try {
      let documentId = id;

      if (id) {
        await updateContentDocument(collectionKey, id, payload, user);
      } else {
        documentId = await createContentDocument(collectionKey, payload, user);
      }

      const mediaPatch = await buildMediaPatch({
        collectionKey,
        files,
        payload,
        documentId,
        currentItem: id ? selectedItem : null,
        setUploadStatus,
      });

      if (Object.keys(mediaPatch).length > 0) {
        await updateContentDocument(collectionKey, documentId, mediaPatch, user);
      }

      setUploadStatus('');
      const refreshed = await reloadItems();
      const current = refreshed.find((item) => item.id === documentId) || null;
      setSelectedItem(current);
    } catch (error) {
      setErrorMessage(error.message ?? 'Failed to save record.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!canDelete) {
      setErrorMessage('Only admin users can delete records.');
      return;
    }

    const shouldDelete = window.confirm(
      `Delete "${item.title}"? This also removes linked storage files.`
    );
    if (!shouldDelete) {
      return;
    }

    setDeletingId(item.id);
    setErrorMessage('');

    try {
      const storagePaths = collectStoragePaths(item);
      for (const path of storagePaths) {
        await deleteStoragePath(path);
      }

      await deleteContentDocument(collectionKey, item.id);
      const refreshed = await reloadItems();

      if (selectedItem?.id === item.id) {
        setSelectedItem(null);
      } else if (selectedItem?.id) {
        const current = refreshed.find((record) => record.id === selectedItem.id) || null;
        setSelectedItem(current);
      }
    } catch (error) {
      setErrorMessage(error.message ?? 'Failed to delete record.');
    } finally {
      setDeletingId('');
    }
  }

  function handleEdit(item) {
    setSelectedItem(item);
    setUploadStatus('');
    setErrorMessage('');
  }

  function handleNew() {
    setSelectedItem(null);
    setUploadStatus('');
    setErrorMessage('');
  }

  if (loading) {
    return <LoadingState label={`Loading ${config?.title}...`} />;
  }

  return (
    <div className="page-stack">
      <section className="section-heading">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h1>{config?.title}</h1>
          <p>{config?.description}</p>
        </div>
      </section>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {uploadStatus ? <p className="upload-status">{uploadStatus}</p> : null}

      <div className="dashboard-toolbar">
        <input
          placeholder={`Search ${config?.title.toLowerCase()}...`}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {selectedItem ? (
          <button type="button" onClick={handleNew}>
            Create new {config?.singular}
          </button>
        ) : null}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-panel">
          <DashboardContentTable
            collectionKey={collectionKey}
            items={filteredItems}
            onEdit={handleEdit}
            onDelete={handleDelete}
            deletingId={deletingId}
            canDelete={canDelete}
          />
        </div>
        <div className="dashboard-panel">
          <ContentEditorForm
            collectionKey={collectionKey}
            initialContent={selectedItem}
            isSaving={isSaving}
            onCancel={handleNew}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}
